import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { IResourceComponentsProps, useTranslate } from "@refinedev/core";
import { Controller } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React from "react";

export const PromocodeCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();

  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm();

  // Generate a random code
  const generateRandomCode = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    const charactersLength = characters.length;
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  // Set default values
  React.useEffect(() => {
    const defaultCode = generateRandomCode();
    const defaultExpiresAt = new Date();
    defaultExpiresAt.setMonth(defaultExpiresAt.getMonth() + 1); // Default 1 month expiration

    // Set default values using setValue from react-hook-form instead of direct DOM manipulation
    setValue("code", defaultCode);
    setValue("duration", 30);
    setValue("maxUses", 100);
  }, [setValue]);

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("code", {
                    required: "This field is required",
                  })}
                  error={!!(errors as any)?.code}
                  helperText={(errors as any)?.code?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label="Promocode"
                  name="code"
                />

                <TextField
                  {...register("duration", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  error={!!(errors as any)?.duration}
                  helperText={(errors as any)?.duration?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Duration (days)"
                  name="duration"
                />

                <TextField
                  {...register("maxUses", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  error={!!(errors as any)?.maxUses}
                  helperText={(errors as any)?.maxUses?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Max Uses"
                  name="maxUses"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Controller
                  control={control}
                  name="expiresAt"
                  defaultValue={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Expires At"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                            error: !!(errors as any)?.expiresAt,
                            helperText: (errors as any)?.expiresAt?.message,
                          },
                        }}
                        minDate={new Date()}
                      />
                    </LocalizationProvider>
                  )}
                />

                <Controller
                  control={control}
                  name="isActive"
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Active"
                      sx={{ marginTop: 2, display: "block" }}
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.checked);
                          }}
                        />
                      }
                    />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Create>
  );
};
